/**
 * EIL — L'UNITÉ A DÉMÉNAGÉ SUR SON PROPRE DOMAINE (29/08/2026).
 *
 * ── Ce que cet écran était, et pourquoi il n'est plus ──────────────────────
 *
 * Il portait toute l'organisation des échanges inter-laboratoires : créer une
 * campagne, inviter, ouvrir, clore, lire les résultats par groupe comparable.
 * Tout cela a été porté sur l'application EIL, qui vit sur SA base MySQL, avec
 * SES comptes et SON journal — décision de la RQ : « passe l'organisation
 * totalement sur 3003 · sépare totalement les utilisateurs de l'EIL de ceux du
 * reste de BIO24 · il faut qu'il soit informatiquement totalement isolé ».
 *
 * ⚠️ POURQUOI L'ÉCRAN EST VIDÉ ET NON SIMPLEMENT LAISSÉ EN PLACE. Les deux
 * moitiés liraient deux bases différentes. Une campagne organisée ici ne
 * verrait aucun dépôt fait là-bas, et réciproquement — et les DEUX
 * paraîtraient fonctionner. C'est le genre de divergence qu'on ne découvre
 * qu'en rapprochant deux listes à la main, des mois plus tard, quand les
 * résultats sont déjà partis en revue de direction.
 *
 * ⚠️ ET POURQUOI IL RESTE UNE PAGE, PLUTÔT QUE RIEN. Une rubrique qui
 * disparaît sans phrase se lit comme une panne : on cherche le menu, on
 * recharge, on appelle. Un renvoi explicite coûte quinze lignes et évite tout
 * cela. La rubrique reste dans la barre — c'est bien un sujet du système
 * qualité (§7.3.7.4) — mais elle ne fait plus qu'indiquer la porte.
 *
 * ⚠️ NE PAS RECÂBLER cet écran sur `/ged/eil*`. Ces routes seront retirées du
 * moteur ; d'ici là, elles ne servent plus rien à l'écran.
 *
 * Les données ont été migrées le 29/08 : 2 campagnes, 9 laboratoires et
 * 10 participations avec leurs dépôts, relues une à une dans la base `eil`.
 */
import React from 'react';
import { EnteteEcran } from '@/components/EnteteEcran';
import { I, card } from '@/components/ui';

const EIL_URL = process.env.NEXT_PUBLIC_EIL_URL || 'http://localhost:3003';

export function Eil() {
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <EnteteEcran rubrique="Plateau technique & contrôles" clauseISO="7.3.7.4"
        titre="EIL — Échange inter-laboratoires" />

      <div style={{ ...card, padding: '14px 16px', fontSize: 13, lineHeight: 1.65,
        display: 'grid', gap: 10, color: 'var(--text-body)' }}>
        <p style={{ margin: 0 }}>
          <strong>Les échanges inter-laboratoires ont leur propre application</strong>, sur une
          base de données séparée du reste de BIO24 — décision du 29 août 2026. Organiser une
          campagne, inviter des confrères, ouvrir, clore et lire les résultats s’y fait
          désormais entièrement.
        </p>

        {/* ⚠️ Le second identifiant est annoncé ICI, avant le clic. Sans cette
            phrase, on arrive là-bas, on tente son mot de passe habituel, on
            échoue, on le change, puis on téléphone. */}
        <p style={{ margin: 0 }}>
          L’accès y demande un <strong>identifiant propre</strong> : votre compte BIO24 n’y
          ouvre rien, et c’est voulu — un laboratoire confrère ne doit avoir aucun compte dans
          notre système qualité, et notre propre participation à l’échange doit être aussi
          traçable que la sienne.
        </p>

        <p style={{ margin: 0 }}>
          <a href={EIL_URL} target="_blank" rel="noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <I n="lien-externe" s={14} />Ouvrir l’application EIL
          </a>
        </p>
      </div>

      <div style={{ ...card, padding: '12px 15px', fontSize: 12.5, lineHeight: 1.6,
        color: 'var(--text-muted)' }}>
        {/* La distinction EEQ / EIL reste dite dans le système qualité : c'est
            ici qu'on arrive en cherchant « contrôles externes », et c'est ici
            qu'on peut encore se tromper de registre. */}
        Un EIL est organisé <strong>par le laboratoire</strong>, entre confrères, sur les
        paramètres qu’aucun abonnement d’EEQ ne couvre. La référence est ce que trouvent les
        participants — moyenne pour un dosage, réponse majoritaire pour une lecture — et non une
        valeur assignée par un organisme accrédité. <strong>Un EIL ne remplace pas un EEQ</strong> :
        quand BIO24 y participe, le résultat est reporté dans ses EEQ en mentionnant que le
        programme est un « EIL local ».
      </div>
    </div>
  );
}
